import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {CaretLeft, CaretRight, UploadSimple} from '../../../icons';
import {OrbFactCloud} from './OrbFactCloud';

export const ORB_VIEW_COUNT = 'count';
export const ORB_VIEW_CLOUD = 'cloud';
export const ORB_VIEW_HELP = 'help';

// Espeja `ClientMemoryBriefEndpoint.FACTS_CAP` del backend: al llegar justo al tope no se sabe si
// son 300 o 900, así que se dice "300+" en vez de afirmar un número que puede ser falso.
export const BRIEF_FACTS_CAP = 300;

/**
 * El conteo de hechos, escrito SIEMPRE igual.
 *
 * Vive acá y se exporta porque el mismo número se dibuja en dos lugares a centímetros de distancia:
 * esta vista y el chip del HUD (`BoosterFeedHero`). Con el tope aplicado en un solo lado, la misma
 * memoria decía «300+» en el orbe y «300» en el chip de al lado — dos afirmaciones distintas sobre
 * el mismo dato, y una de ellas falsa.
 *
 * `null` es "todavía no llegó" (o "no lo pude leer"), y eso se dibuja como un guion: un cero ahí
 * afirmaría que el cliente no sabe nada.
 */
export function formatFactCount(count, truncated) {
    if (typeof count !== 'number') return '—';
    // El `+` sale de si la LISTA vino recortada, no de compararla contra un tope adivinado.
    // Cuando el número lo daba el brief no había otra forma: su cap es 300 y llegar a 300 podía
    // ser 300 o 900. Desde que sale del store, el recorte viene con bandera propia — y con el tope
    // hardcodeado, un cliente con 312 aprobados leía «312+» siendo exacto.
    // El fallback conserva el comportamiento viejo para quien todavía pasa el conteo del brief.
    const isCapped = truncated === undefined ? count >= BRIEF_FACTS_CAP : truncated;
    return `${count}${isCapped ? '+' : ''}`;
}

/**
 * Las tres vistas que giran alrededor del orbe.
 *
 * El orbe sigue siendo `<canvas>` puro (`BoosterOrb.js`) y este componente NO lo toca: se monta
 * como hermano suyo, adentro de `.iab-orb`, que es una grilla de una sola celda. Las tres vistas
 * comparten esa celda (`grid-area: 1 / 1` en el SCSS), así que se apilan sobre el dibujo sin
 * moverlo y sin necesitar un contenedor propio — por eso el componente devuelve un fragmento.
 *
 * Qué se defiende acá:
 *
 * 1. **El conteo es la vista de entrada.** Es lo que la pantalla venía mostrando y lo que el
 *    usuario espera ver al llegar; las otras dos son un descubrimiento, no un requisito.
 * 2. **La nube sólo se OFRECE si hay hechos.** Sin brief (o con la memoria vacía) no hay tercera
 *    vista: un dot que lleva a una esfera vacía promete algo que no existe. Por eso las vistas se
 *    arman en runtime y el índice se reencuadra si la lista cambia de largo.
 * 3. **Flechas y dots son `<button>` de verdad.** Se llega con Tab, se activan con Enter y anuncian
 *    a dónde llevan; un `<div onClick>` no hace ninguna de las tres cosas.
 * 4. **La zona sigue recibiendo material.** Los handlers de drop viven en `.iab-orb` (el padre) y
 *    esto no los intercepta: los botones no llaman a `preventDefault` de drag y el drop burbujea.
 */
export function BoosterOrbCarousel({
    clientName,
    factCount = null,
    // ¿La lista de la que sale el conteo vino recortada? Sin esto el `+` se adivinaba comparando
    // contra el tope del brief, que ya no es la fuente del número.
    factCountTruncated,
    briefFailed = false,
    facts = null,
}) {
    const {t} = useTranslation();
    const [index, setIndex] = useState(0);
    // Sentido del último movimiento: es lo que decide de qué lado entra la vista nueva. Va en un
    // ref y no en el estado porque no dispara render por sí solo — lo consume el render que ya
    // provoca el cambio de índice.
    const directionRef = useRef(1);

    const countLoading = factCount === null && !briefFailed;
    const hasFacts = Array.isArray(facts) && facts.length > 0;

    const views = [ORB_VIEW_COUNT];
    if (hasFacts) views.push(ORB_VIEW_CLOUD);
    views.push(ORB_VIEW_HELP);

    // El brief llega después del primer render: la nube aparece en el medio de la lista y el índice
    // que apuntaba a la ayuda pasaría a apuntar a la nube. Se reencuadra por CLAVE, no por posición.
    const viewKeyRef = useRef(views[0]);
    useEffect(() => {
        const wanted = viewKeyRef.current;
        const at = views.indexOf(wanted);
        if (at === -1) {
            setIndex(0);
            viewKeyRef.current = views[0];
        } else if (at !== index) {
            setIndex(at);
        }
        // `views` se recalcula en cada render (es un array nuevo): se depende de su CONTENIDO.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [views.join('|')]);

    // Wrap-around en los dos sentidos: el módulo se normaliza a positivo porque `-1 % 3` en JS es
    // `-1`, no `2`, y con eso la flecha izquierda desde la primera vista no llevaría a ningún lado.
    const go = (delta) => {
        directionRef.current = delta >= 0 ? 1 : -1;
        const next = ((index + delta) % views.length + views.length) % views.length;
        viewKeyRef.current = views[next];
        setIndex(next);
    };

    const goTo = (target) => {
        if (target === index) return;
        go(target - index);
    };

    const active = views[index] || ORB_VIEW_COUNT;
    const enterClass = directionRef.current > 0 ? '-enter-right' : '-enter-left';

    const dotLabel = {
        [ORB_VIEW_COUNT]: t('ia_booster.feed.carousel_dot_count'),
        [ORB_VIEW_CLOUD]: t('ia_booster.feed.carousel_dot_cloud'),
        [ORB_VIEW_HELP]: t('ia_booster.feed.carousel_dot_help'),
    };

    return (
        <>
            {/* La vista activa se remonta con `key`: es lo que hace que la animación de entrada
                vuelva a correr en cada cambio (una clase sobre el mismo nodo no reinicia nada). */}
            <div className={`iab-orbview ${enterClass}`} key={active} data-view={active}>
                {active === ORB_VIEW_COUNT && (
                    <div className="iab-orb-core">
                        <div className={`iab-orb-count ${countLoading ? '-loading' : ''}`}>
                            {formatFactCount(factCount, factCountTruncated)}
                        </div>
                        <div className="iab-orb-cap">
                            {t('ia_booster.orb.caption', {
                                client: clientName || t('ia_booster.this_client'),
                                interpolation: {escapeValue: false},
                            })}
                        </div>
                        <div className="iab-orb-sub">
                            {factCount === 0
                                ? t('ia_booster.orb.sub_empty')
                                : t('ia_booster.orb.sub')}
                        </div>
                    </div>
                )}

                {active === ORB_VIEW_CLOUD && <OrbFactCloud facts={facts}/>}

                {active === ORB_VIEW_HELP && (
                    <div className="iab-orbhelp">
                        <span className="iab-orbhelp-ic" aria-hidden="true">
                            <UploadSimple size={22}/>
                        </span>
                        <p className="iab-orbhelp-text">
                            {t('ia_booster.feed.help_text', {
                                client: clientName || t('ia_booster.this_client'),
                                interpolation: {escapeValue: false},
                            })}
                        </p>
                        {/* Sin la lista de extensiones (`feed.help_hint`): sobre el orbe, en 250px
                            de ancho, «.txt, .vtt o .md» era medio choclo y no decía nada que el
                            error del momento no diga mejor. Queda la frase que explica el gesto. */}
                    </div>
                )}
            </div>

            <button type="button" className="iab-orb-nav -left"
                    onClick={() => go(-1)}
                    aria-label={t('ia_booster.feed.carousel_prev')}>
                <CaretLeft size={16} weight="bold"/>
            </button>
            <button type="button" className="iab-orb-nav -right"
                    onClick={() => go(1)}
                    aria-label={t('ia_booster.feed.carousel_next')}>
                <CaretRight size={16} weight="bold"/>
            </button>

            <div className="iab-orb-dots" role="group" aria-label={t('ia_booster.feed.carousel_aria')}>
                {views.map((view, i) => (
                    <button
                        key={view}
                        type="button"
                        className={`iab-orb-dot ${i === index ? '-on' : ''}`}
                        aria-label={dotLabel[view]}
                        aria-current={i === index ? 'true' : undefined}
                        onClick={() => goTo(i)}
                    />
                ))}
            </div>
        </>
    );
}

export default BoosterOrbCarousel;
